/** Does this tenant go live through its easy setup?
 *
 *  An easy-onboarding tenant still under the outbound guard (`go_live.outbound_comms_blocked`)
 *  goes live from its easy setup, which lifts the guard and sets the launch policy together.
 *  Every console path that only switches a launch policy (the Run board's tenant-default
 *  setter, its "Move these tours" link, the Defaults tab's default launch policy) would leave
 *  every automatic guest message blocked for good, so those paths ask here first.
 *
 *  The easy state needs console_top_manage + console_preflight_manage (the same permissions
 *  as the setter), so the caller says whether the viewer may ask (`enabled`). A tenant that is
 *  not easy answers 409 `not_easy` and keeps its paths unchanged; a live easy tenant too. Until
 *  the check answers, nothing is offered on a guess: `open` is false, and `unknown` says the
 *  check failed (offer `retry`). */
import { useQuery } from '@tanstack/react-query';

import * as easyApi from '@/api/easyOnboarding';

export interface EasyGoLiveGate {
  /** The tenant is easy and still blocked: send the operator to its easy setup's Go live. */
  blocked: boolean;
  /** The check answered: not easy, or easy and live. The policy-only paths may be offered. */
  open: boolean;
  /** The check was asked and did not answer: offer no policy-only path, and a Try again. */
  unknown: boolean;
  retry: () => void;
}

/** Where an easy tenant goes live. */
export const easyOnboardHref = (topId: number) => `/tour-operators/${topId}/onboard`;

export function useEasyGoLiveGate(topId: number, enabled: boolean): EasyGoLiveGate {
  const easy = useQuery({
    queryKey: easyApi.easyStateKey(topId),
    queryFn: ({ signal }) => easyApi.getState(topId, signal),
    enabled,
    retry: false,
    staleTime: 10_000,
  });
  const notEasy = easyApi.easyErrorCode(easy.error) === easyApi.EASY_ERROR.notEasy;
  const blocked = enabled && easy.data?.go_live.outbound_comms_blocked === true;
  return {
    blocked,
    open: enabled && (notEasy || (easy.data != null && !blocked)),
    unknown: enabled && !easy.isLoading && !notEasy && easy.data == null,
    retry: () => void easy.refetch(),
  };
}
